import type { AssetView, CanvasDocumentV3 as CanvasDocument, CanvasNodeV3 as CanvasNode } from "@seedance/contracts";
import type { Viewport } from "@xyflow/react";
import { applyCanvasCommand, createCanvasHistory } from "./model/history.js";

export interface Point {
  x: number;
  y: number;
}

export type PendingAssetInput =
  | { source: "drop"; files: File[]; canvasPoint: Point }
  | { source: "paste"; files: File[]; canvasPoint: Point }
  | { source: "library"; assets: AssetView[]; canvasPoint: Point };

const placementStep = 32;
const assetCardWidth = 320;
const assetCardHeight = 200;
const assetCardGap = 32;
const assetRowsPerColumn = 2;

interface AssetNodeBounds extends Point {
  width: number;
  height: number;
}

export function normalizePendingAssetInput<T extends PendingAssetInput>(input: T): T {
  const values = input.source === "library" ? input.assets : input.files;
  if (values.length === 0) throw new Error("At least one asset is required");
  return {
    ...input,
    ...(input.source === "library"
      ? { assets: [...input.assets] }
      : { files: [...input.files] })
  } as T;
}

export function offsetCanvasPoint(anchor: Point, index: number): Point {
  return {
    x: anchor.x + index * placementStep,
    y: anchor.y + index * placementStep
  };
}

function uploadedAssetPoint(anchor: Point, index: number): Point {
  return {
    x: anchor.x + Math.floor(index / assetRowsPerColumn) * (assetCardWidth + assetCardGap),
    y: anchor.y + (index % assetRowsPerColumn) * (assetCardHeight + assetCardGap)
  };
}

function intersects(left: AssetNodeBounds, right: AssetNodeBounds): boolean {
  return left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y;
}

function nextAvailableAssetPoint(
  desired: Point,
  size: { width: number; height: number },
  occupied: readonly AssetNodeBounds[]
): Point {
  const directions = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: -1, y: 0 },
    { x: 0, y: -1 },
    { x: 1, y: 1 },
    { x: -1, y: 1 },
    { x: -1, y: -1 },
    { x: 1, y: -1 }
  ];
  for (let index = 0; index < 49; index += 1) {
    const ring = index < directions.length ? 1 : Math.floor((index - 1) / 8) + 1;
    const direction = index < directions.length
      ? directions[index]!
      : directions[1 + ((index - 1) % 8)]!;
    const candidate = {
      x: desired.x + direction.x * ring * (assetCardWidth + assetCardGap),
      y: desired.y + direction.y * ring * (assetCardHeight + assetCardGap),
      width: size.width,
      height: size.height
    };
    if (!occupied.some((node) => intersects(candidate, node))) {
      return { x: candidate.x, y: candidate.y };
    }
  }
  return desired;
}

export function canvasPointAtViewportCenter({
  bounds,
  viewport
}: {
  bounds: { width: number; height: number };
  viewport: Viewport;
}): Point {
  return {
    x: (bounds.width / 2 - viewport.x) / viewport.zoom,
    y: (bounds.height / 2 - viewport.y) / viewport.zoom
  };
}

export function clientPointToCanvas({
  clientPoint,
  bounds,
  viewport
}: {
  clientPoint: Point;
  bounds: Pick<DOMRect, "left" | "top">;
  viewport: Viewport;
}): Point {
  return {
    x: (clientPoint.x - bounds.left - viewport.x) / viewport.zoom,
    y: (clientPoint.y - bounds.top - viewport.y) / viewport.zoom
  };
}

export function placeUploadedAssets({
  anchor,
  assets,
  createId = () => crypto.randomUUID()
}: {
  anchor: Point;
  assets: readonly AssetView[];
  createId?: () => string;
}): CanvasNode[] {
  return assets.map((asset, index) => ({
    id: createId(),
    kind: asset.kind,
    assetId: asset.id,
    // A small cascade looked compact but made every later card cover the
    // previous header, which breaks direct multi-selection on touch devices.
    position: uploadedAssetPoint(anchor, index),
    size:
      asset.kind === "audio"
        ? { width: 300, height: 96 }
        : { width: 320, height: 200 },
    collapsed: false
  }));
}

export function appendPlacedAssets(
  document: CanvasDocument,
  assets: readonly AssetView[],
  anchor: Point,
  createId: () => string = () => crypto.randomUUID()
): CanvasDocument {
  const occupied = document.nodes.map((node) => ({
    x: node.position.x,
    y: node.position.y,
    width: node.size.width,
    height: node.size.height
  }));
  const nodes = placeUploadedAssets({ anchor, assets, createId }).map((node) => {
    const position = nextAvailableAssetPoint(node.position, node.size, occupied);
    occupied.push({ x: position.x, y: position.y, width: node.size.width, height: node.size.height });
    return { ...node, position };
  });
  let history = createCanvasHistory(document);
  for (const node of nodes) {
    history = applyCanvasCommand(history, { type: "add_node", node });
  }
  return history.present;
}

export function placeLibraryAssets({
  document,
  assets,
  anchor,
  touchAsset,
  onTouchFailure
}: {
  document: CanvasDocument;
  assets: readonly AssetView[];
  anchor: Point;
  touchAsset(assetId: string): Promise<AssetView>;
  onTouchFailure?(asset: AssetView): void;
}): CanvasDocument {
  const next = appendPlacedAssets(document, assets, anchor);
  for (const asset of assets) {
    void touchAsset(asset.id).catch(() => onTouchFailure?.(asset));
  }
  return next;
}

export interface TouchRetryQueue {
  add(asset: AssetView): void;
  retry(assetId: string): Promise<void>;
  getFailures(): AssetView[];
  subscribe(listener: (assets: AssetView[]) => void): () => void;
}

export function createTouchRetryQueue({
  touchAsset,
  onSuccess
}: {
  touchAsset(assetId: string): Promise<AssetView>;
  onSuccess?(asset: AssetView): void;
}): TouchRetryQueue {
  const failures = new Map<string, { asset: AssetView; version: number }>();
  const retrying = new Map<string, Promise<void>>();
  const listeners = new Set<(assets: AssetView[]) => void>();
  let version = 0;
  const snapshot = () => Array.from(failures.values(), ({ asset }) => asset);
  const notify = () => {
    const assets = snapshot();
    listeners.forEach((listener) => listener(assets));
  };
  const add = (asset: AssetView) => {
    failures.set(asset.id, { asset, version: ++version });
    notify();
  };
  const retry = (assetId: string): Promise<void> => {
    const existing = retrying.get(assetId);
    if (existing) return existing;
    const failure = failures.get(assetId);
    if (!failure) return Promise.resolve();
    const promise = touchAsset(assetId)
      .then((asset) => {
        if (failures.get(assetId)?.version === failure.version) {
          failures.delete(assetId);
        }
        onSuccess?.(asset);
        notify();
      })
      .catch(() => {
        // Each failure remains independently retryable.
      })
      .finally(() => {
        if (retrying.get(assetId) === promise) retrying.delete(assetId);
      });
    retrying.set(assetId, promise);
    return promise;
  };

  return {
    add,
    retry,
    getFailures: snapshot,
    subscribe(listener) {
      listeners.add(listener);
      listener(snapshot());
      return () => listeners.delete(listener);
    }
  };
}
